'use strict';

import { flow, observable, configure, action, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import appStore from './AppStore';
import {States} from './AppConstants';
import events from './Events';
import NowDateTimeEvents from './NowDateTimeEvents';
import {getAggregateState} from "./StateUtils";
import multiCallUtil from "./MultiCallUtil";
import {isFalse, isEmpty} from "./Utils";

class TermStore {

    state = States.initial;

    constructor() {
        makeObservable(this, {
            state: observable,
            onSetNow: action,
            load: action,
            checkDataLoadStates: action
        });

        this.baseUrl = `api/term/terms`;

        this.dataLoadStates = {};

        this.termTypes = {
            currentPlanableTerm: {
                method: "getTerm",
                param: "currentplanable"
            },
            currentTerm: {
                method: "getTerm",
                param: "current"
            },
            publishedTerms: {
                method: "getTerms",
                param: "published"
            },
            futureTerms: {
                method: "getTerms",
                param: "future"
            }
        }

        events.listen(NowDateTimeEvents.setNow, (newStartTime) => this.onSetNow(newStartTime))
    }

    /**
     * Listener for now time widget updates.
     * @param newStartTime The new time that was selected from the drop down mock dates.
     */
    onSetNow(newStartTime) {
        this.state = States.pending;

        if (appStore.isDebug()) {
            console.log(`[TermStore] onSetNow: setting now time [${newStartTime}]`);
        }

        let loadTypes = [];
        if ( !isEmpty( appStore.getMockCurrentPlannableTerm() ) ) {
            this["currentPlanableTerm"] = appStore.getMockCurrentPlannableTermInfo();
        } else {
            loadTypes.push("currentPlanableTerm");
        }

        if ( !isEmpty( appStore.getMockCurrentTerm() ) ) {
            this["currentTerm"] = appStore.getMockCurrentTermInfo();
        } else {
            loadTypes.push("currentTerm");
        }

        if( !isEmpty( appStore.getMockPublishedTerms() ) ) {
            this["publishedTerms"] = appStore.getMockPublishedTermInfos();
        } else {
            loadTypes.push("publishedTerms");
        }

        if(loadTypes.length > 0) {
            this.load(loadTypes);
        } else {
            this.state = States.done;
        }

    }

    /**
     * Returns the term data saved in the store based on type
     * @param type The type of term data to return
     * @returns {undefined|object|array} if type is not found returns undefined, otherwise returns object/array of term data
     */
    get = (type) => {
        if (this.hasOwnProperty(type)) {
            return this[type];
        } else {
            return undefined;
        }
    }

    /**
     * Returns an object of term data for a single term based on termId and the type of term data
     * @param termId the term id of desired term data to return
     * @param type The type of term data to retrieve the term data from (like "futureTerms" or "publishedTerms")
     * @returns {undefined|object} a single term object or if not found undefined
     */
    getTermId = (termId, type) => {
        // is the term type a list of terms or a single term
        if (this.termTypes[type].method === 'getTerms') {
            // find term id from list of terms
            return this[type].find(term => term.id === termId);
        } else {
            // return single term if term id matches from the term type
            return this[type].id === termId ?? undefined;
        }
    }

    /**
     * Make the API call to get the data for a list of terms
     * @param filter the type of term data to retrieve (like "published", "future", "planning", "inprogress")
     * @returns {Promise<*>}
     */
    async getTerms(filter) {
        let url = `${this.baseUrl}?filter=${filter}`;
        return await appStore.getHttpClient().doGet(url, 'getTerms');
    }

    /**
     * Make the API call to get the term data for a single term
     * @param termId either a valid term id or term name (like "current" or "currentplanable")
     * @returns {Promise<*>}
     */
    async getTerm(termId) {
        let url = `${this.baseUrl}/${termId}`;
        return await appStore.getHttpClient().doGet(url, 'getTerm');
    }

    /**
     * Action to create list of calls to make to fetch term data
     * @param termTypes accepts either a single term type via a string or a list of strings for multiple term data types to fetch
     */
    load = (termTypes) => {
        this.state = States.pending;

        let loadTypes = [];
        switch (typeof termTypes) {
            case 'string':
                loadTypes.push(termTypes);
                break;
            case 'object':
                loadTypes = termTypes;
                break;
            default:
                return;
        }

        let multiFetch = [];
        // iterate over list of term types to
        loadTypes.map(type => {
            // if the term type is a known type
            if (this.termTypes.hasOwnProperty(type)) {
                // if there is no data load state for that type (has not been previously fetched)
                if (isFalse(this.dataLoadStates.hasOwnProperty(type)) ) {
                    // set the type data load state to initial
                    this.dataLoadStates[type] = States.initial;
                    // add a function to fetch the term data to the multi fetch
                    multiFetch.push(() => this.loadTerms(type));
                } else {
                    // attempt to fetch is either already completed or is inflight
                    if (appStore.isDebug()) console.log(`[TermStore] ${type}: cache being used`);
                }
            } else {
                // term type specified doesn't exist, don't try to fetch and give a warning
                if (appStore.isDebug()) console.warn(`[TermStore] ${type}: term type is not valid`);
            }
        });

        // if there are term(s) to load, make the call(s)
        if (multiFetch.length > 0) {
            multiCallUtil(multiFetch);
        } else {
            // the term types specified to load have all already been previously completed or don't exist
            this.checkDataLoadStates();
        }
    };

    /**
     * Makes either the getTerm or getTerms to fetch term data of a certain type
     * @param type the term type to fetch
     */
    loadTerms = flow(function* (type) {
        this.dataLoadStates[type] = States.pending;
        this.checkDataLoadStates();

        if (appStore.isDebug()) console.log(`[TermStore] ${type}: load started`);
        try {
            this[type] = yield this[this.termTypes[type].method](this.termTypes[type].param);
            if (appStore.isDebug()) console.log(`[TermStore] ${type}: load completed`);
            this.dataLoadStates[type] = States.done;
        } catch(error) {
            appStore.getExceptionUtils().handleException(error, "loadTerms");
            this.dataLoadStates[type] = States.error;
        }
        this.checkDataLoadStates();
    }.bind(this));

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }
}

export default new TermStore();